import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PropertiesPanelComponent } from '../properties-panel/properties-panel.component';
import { LayersPanelComponent } from '../layers-panel/layers-panel.component';
import { GalleryPanelComponent } from '../gallery-panel/gallery-panel.component';
import { CanvasService } from '../../services/canvas.service';
import { ExportImportService } from '../../services/export-import.service';

type TabType = 'properties' | 'layers' | 'gallery';

@Component({
    selector: 'app-right-panel',
    imports: [CommonModule, PropertiesPanelComponent, LayersPanelComponent, GalleryPanelComponent],
    templateUrl: './right-panel.component.html',
    styleUrl: './right-panel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RightPanelComponent {
    private canvasService = inject(CanvasService);
    private exportImportService = inject(ExportImportService);
    activeTab = signal<TabType>('properties');
    readonly isVisitingCard = computed(() => this.canvasService.canvasSizeTypeValue() === 'visiting-card');
    readonly isEnvelope = computed(() => this.canvasService.canvasSizeTypeValue() === 'cover-9-5x4-25');
    readonly isLetterhead = computed(() => this.canvasService.canvasSizeTypeValue() === 'letterhead-a4');
    readonly activeSide = this.canvasService.activeSideValue;

    setActiveSide(side: 'front' | 'back'): void {
        this.canvasService.setActiveSide(side);
    }

    addVisitingCardSample(variant: 'classic' | 'dark' | 'minimal' | 'corporate'): void {
        this.resetCanvasView();

        const canvasSize = this.canvasService.canvasSize();
        const width = canvasSize.width;
        const height = canvasSize.height;
        const margin = Math.max(6, Math.round(Math.min(width, height) * 0.06));
        const accentHeight = Math.max(18, Math.round(height * 0.28));
        const side = this.canvasService.requestSideForNewObject();

        if (variant === 'classic') {
            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width, height },
                fill: { r: 248, g: 248, b: 250, a: 1 },
                stroke: { color: { r: 210, g: 210, b: 210, a: 1 }, width: 1 },
                side
            });

            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width, height: accentHeight },
                fill: { r: 27, g: 90, b: 200, a: 1 },
                side
            });

            this.addText({
                x: margin,
                y: Math.round(accentHeight * 0.22),
                width: width - margin * 2,
                height: 24,
                content: 'Karan Mehta',
                color: { r: 255, g: 255, b: 255, a: 1 },
                fontSize: Math.max(14, Math.round(height * 0.16)),
                fontWeight: 700,
                side
            });

            this.addText({
                x: margin,
                y: Math.round(accentHeight * 0.65),
                width: width - margin * 2,
                height: 18,
                content: 'Creative Designer',
                color: { r: 230, g: 238, b: 255, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            const infoStartY = accentHeight + Math.round(height * 0.12);
            const infoGap = Math.max(12, Math.round(height * 0.18));

            this.addText({
                x: margin,
                y: infoStartY,
                width: width - margin * 2,
                height: 16,
                content: '+91 98765 43210',
                color: { r: 40, g: 40, b: 40, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            this.addText({
                x: margin,
                y: infoStartY + infoGap,
                width: width - margin * 2,
                height: 16,
                content: 'hello@nova-studio.com',
                color: { r: 40, g: 40, b: 40, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            this.addText({
                x: margin,
                y: infoStartY + infoGap * 2,
                width: width - margin * 2,
                height: 16,
                content: 'novastudio.in',
                color: { r: 40, g: 40, b: 40, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });
            return;
        }

        if (variant === 'dark') {
            const darkGap = Math.max(6, Math.round(height * 0.12));

            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width, height },
                fill: { r: 22, g: 24, b: 28, a: 1 },
                side
            });

            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: margin, y: height - darkGap },
                size: { width: width - margin * 2, height: 3 },
                fill: { r: 255, g: 168, b: 60, a: 1 },
                side
            });

            this.addText({
                x: margin,
                y: Math.round(height * 0.18),
                width: width - margin * 2,
                height: 24,
                content: 'Riya Kapoor',
                color: { r: 245, g: 245, b: 245, a: 1 },
                fontSize: Math.max(14, Math.round(height * 0.17)),
                fontWeight: 700,
                side
            });

            this.addText({
                x: margin,
                y: Math.round(height * 0.4),
                width: width - margin * 2,
                height: 18,
                content: 'Brand Strategist',
                color: { r: 180, g: 190, b: 205, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            const contactY = Math.round(height * 0.62);
            const contactGap = Math.max(10, Math.round(height * 0.16));

            this.addText({
                x: margin,
                y: contactY,
                width: width - margin * 2,
                height: 16,
                content: '+91 90000 11122',
                color: { r: 245, g: 245, b: 245, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            this.addText({
                x: margin,
                y: contactY + contactGap,
                width: width - margin * 2,
                height: 16,
                content: 'studio@midnight.co',
                color: { r: 245, g: 245, b: 245, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            this.addText({
                x: margin,
                y: contactY + contactGap * 2,
                width: width - margin * 2,
                height: 16,
                content: 'midnight.co',
                color: { r: 245, g: 245, b: 245, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });
            return;
        }

        if (variant === 'corporate') {
            const leftStrip = Math.max(10, Math.round(width * 0.08));
            const redBandHeight = Math.max(14, Math.round(height * 0.22));
            const bandY = Math.round(height * 0.42);
            const rightColumnX = Math.round(width * 0.62);

            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width, height },
                fill: { r: 255, g: 255, b: 255, a: 1 },
                stroke: { color: { r: 210, g: 210, b: 210, a: 1 }, width: 1 },
                side
            });

            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width: leftStrip, height },
                fill: { r: 230, g: 230, b: 230, a: 1 },
                side
            });

            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: leftStrip, y: bandY },
                size: { width: width - leftStrip, height: redBandHeight },
                fill: { r: 198, g: 35, b: 44, a: 1 },
                side
            });

            this.addText({
                x: leftStrip + Math.round(width * 0.06),
                y: Math.round(height * 0.12),
                width: width * 0.5,
                height: 18,
                content: 'Full Name',
                color: { r: 200, g: 40, b: 44, a: 1 },
                fontSize: Math.max(12, Math.round(height * 0.14)),
                fontWeight: 700,
                side
            });

            this.addText({
                x: leftStrip + Math.round(width * 0.06),
                y: Math.round(height * 0.22),
                width: width * 0.5,
                height: 14,
                content: 'Job Title',
                color: { r: 80, g: 80, b: 80, a: 1 },
                fontSize: Math.max(9, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            this.addText({
                x: leftStrip + Math.round(width * 0.06),
                y: Math.round(height * 0.3),
                width: width * 0.5,
                height: 14,
                content: 'Email / Other',
                color: { r: 120, g: 120, b: 120, a: 1 },
                fontSize: Math.max(9, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            this.addText({
                x: leftStrip + Math.round(width * 0.5),
                y: bandY + Math.round(redBandHeight * 0.2),
                width: width * 0.45,
                height: 16,
                content: 'Company Name',
                color: { r: 255, g: 255, b: 255, a: 1 },
                fontSize: Math.max(11, Math.round(height * 0.12)),
                fontWeight: 700,
                side
            });

            const lowerLeftY = bandY + redBandHeight + Math.round(height * 0.08);
            const lowerGap = Math.max(10, Math.round(height * 0.14));

            this.addText({
                x: leftStrip + Math.round(width * 0.06),
                y: lowerLeftY,
                width: width * 0.5,
                height: 14,
                content: 'Address Line 1',
                color: { r: 120, g: 120, b: 120, a: 1 },
                fontSize: Math.max(9, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            this.addText({
                x: leftStrip + Math.round(width * 0.06),
                y: lowerLeftY + lowerGap,
                width: width * 0.5,
                height: 14,
                content: 'Address Line 2',
                color: { r: 120, g: 120, b: 120, a: 1 },
                fontSize: Math.max(9, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            const rightStartY = bandY + Math.round(redBandHeight * 1.1);
            const rightGap = Math.max(10, Math.round(height * 0.14));

            this.addText({
                x: rightColumnX,
                y: rightStartY,
                width: width - rightColumnX - margin,
                height: 14,
                content: 'Company Message',
                color: { r: 120, g: 120, b: 120, a: 1 },
                fontSize: Math.max(9, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            this.addText({
                x: rightColumnX,
                y: rightStartY + rightGap,
                width: width - rightColumnX - margin,
                height: 14,
                content: 'Phone / Other',
                color: { r: 120, g: 120, b: 120, a: 1 },
                fontSize: Math.max(9, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            return;
        }
        if (variant === 'minimal') {
            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width, height },
                fill: { r: 255, g: 255, b: 255, a: 1 },
                stroke: { color: { r: 220, g: 220, b: 220, a: 1 }, width: 1 },
                side
            });

            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width: Math.max(12, Math.round(width * 0.12)), height },
                fill: { r: 242, g: 178, b: 61, a: 1 },
                side
            });

            this.addText({
                x: Math.round(width * 0.18),
                y: Math.round(height * 0.2),
                width: width - Math.round(width * 0.2),
                height: 22,
                content: 'Arjun Patel',
                color: { r: 20, g: 20, b: 20, a: 1 },
                fontSize: Math.max(14, Math.round(height * 0.16)),
                fontWeight: 700,
                side
            });

            this.addText({
                x: Math.round(width * 0.18),
                y: Math.round(height * 0.4),
                width: width - Math.round(width * 0.2),
                height: 18,
                content: 'UI/UX Consultant',
                color: { r: 90, g: 90, b: 90, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            const minimalStart = Math.round(height * 0.62);
            const minimalGap = Math.max(10, Math.round(height * 0.16));

            this.addText({
                x: Math.round(width * 0.18),
                y: minimalStart,
                width: width - Math.round(width * 0.2),
                height: 16,
                content: '+91 98989 12121',
                color: { r: 30, g: 30, b: 30, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });

            this.addText({
                x: Math.round(width * 0.18),
                y: minimalStart + minimalGap,
                width: width - Math.round(width * 0.2),
                height: 16,
                content: 'hello@brightlane.in',
                color: { r: 30, g: 30, b: 30, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.1)),
                fontWeight: 500,
                side
            });
        }
    }

    async addVisitingCardAsset(side: 'front' | 'back'): Promise<void> {
        this.resetCanvasView();
        this.canvasService.setActiveSide(side);

        try {
            const sampleName = side === 'front' ? 'vc-ai-sample-front' : 'vc-ai-sample-back';
            const url = new URL(`/assets/visiting-card-sample/${sampleName}.svg`, window.location.origin).href;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch visiting card SVG (${response.status})`);
            }
            const blob = await response.blob();
            const file = new File([blob], `visiting-card-${side}.svg`, { type: 'image/svg+xml' });
            const id = await this.exportImportService.importSVG(file);
            this.canvasService.updateObject(id, { side });
            this.canvasService.selectObject(id);
        } catch (error) {
            console.error('Failed to add visiting card SVG:', error);
            alert('Failed to add visiting card SVG');
        }
    }

    addEnvelopeSample(variant: 'sunset' | 'swirl' | 'geo' | 'minimal'): void {
        this.resetCanvasView();

        const canvasSize = this.canvasService.canvasSize();
        const width = canvasSize.width;
        const height = canvasSize.height;
        const margin = Math.max(12, Math.round(width * 0.08));

        if (variant === 'sunset') {
            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width, height },
                fill: { r: 243, g: 231, b: 223, a: 1 }
            });

            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width, height: Math.round(height * 0.35) },
                fill: { r: 232, g: 199, b: 178, a: 1 }
            });

            this.addText({
                x: margin,
                y: Math.round(height * 0.16),
                width: width * 0.5,
                height: 16,
                content: 'Company Name',
                color: { r: 141, g: 122, b: 111, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.12)),
                fontWeight: 600
            });
            return;
        }

        if (variant === 'swirl') {
            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width, height },
                fill: { r: 255, g: 255, b: 255, a: 1 }
            });

            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width: Math.round(width * 0.18), height },
                fill: { r: 230, g: 132, b: 54, a: 1 }
            });

            this.canvasService.addObject({
                type: 'circle',
                position: { x: Math.round(width * 0.05), y: Math.round(height * 0.18) },
                size: { width: Math.round(height * 0.35), height: Math.round(height * 0.35) },
                radius: Math.round(height * 0.17),
                fill: { r: 245, g: 208, b: 152, a: 1 }
            } as any);

            this.addText({
                x: Math.round(width * 0.28),
                y: Math.round(height * 0.18),
                width: width - Math.round(width * 0.3),
                height: 16,
                content: 'Company Name',
                color: { r: 140, g: 110, b: 90, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.12)),
                fontWeight: 600
            });
            return;
        }

        if (variant === 'geo') {
            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width, height },
                fill: { r: 255, g: 255, b: 255, a: 1 }
            });

            this.canvasService.addObject({
                type: 'polygon',
                position: { x: 0, y: 0 },
                size: { width, height },
                points: [
                    { x: 0, y: 0 },
                    { x: width * 0.2, y: 0 },
                    { x: 0, y: height * 0.4 }
                ],
                closed: true,
                fill: { r: 245, g: 182, b: 72, a: 1 }
            } as any);

            this.canvasService.addObject({
                type: 'polygon',
                position: { x: 0, y: 0 },
                size: { width, height },
                points: [
                    { x: width * 0.7, y: 0 },
                    { x: width, y: 0 },
                    { x: width, y: height * 0.3 }
                ],
                closed: true,
                fill: { r: 240, g: 214, b: 120, a: 1 }
            } as any);

            this.addText({
                x: Math.round(width * 0.35),
                y: Math.round(height * 0.4),
                width: width * 0.5,
                height: 16,
                content: 'Company Name',
                color: { r: 90, g: 90, b: 90, a: 1 },
                fontSize: Math.max(10, Math.round(height * 0.12)),
                fontWeight: 600
            });
            return;
        }

        this.canvasService.addObject({
            type: 'rectangle',
            position: { x: 0, y: 0 },
            size: { width, height },
            fill: { r: 248, g: 248, b: 248, a: 1 },
            stroke: { color: { r: 210, g: 210, b: 210, a: 1 }, width: 1 }
        });

        this.canvasService.addObject({
            type: 'rectangle',
            position: { x: 0, y: height - Math.round(height * 0.18) },
            size: { width: Math.round(width * 0.6), height: Math.round(height * 0.18) },
            fill: { r: 205, g: 205, b: 205, a: 1 }
        });

        this.addText({
            x: margin,
            y: Math.round(height * 0.16),
            width: width * 0.5,
            height: 16,
            content: 'Company Name',
            color: { r: 120, g: 120, b: 120, a: 1 },
            fontSize: Math.max(10, Math.round(height * 0.11)),
            fontWeight: 600
        });
    }

    addLetterheadSample(variant: 'classic' | 'bold' | 'clean' | 'accent'): void {
        this.resetCanvasView();

        const canvasSize = this.canvasService.canvasSize();
        const width = canvasSize.width;
        const height = canvasSize.height;
        const margin = Math.max(28, Math.round(width * 0.08));
        const titleSize = Math.max(16, Math.round(height * 0.04));
        const subtitleSize = Math.max(10, Math.round(height * 0.02));
        const bodySize = Math.max(10, Math.round(height * 0.018));
        const footerSize = Math.max(9, Math.round(height * 0.016));
        const bodyStartY = Math.round(height * 0.26);
        const bodyGap = Math.max(16, Math.round(height * 0.035));
        const footerY = height - Math.round(height * 0.1);

        if (variant === 'classic') {
            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width, height },
                fill: { r: 255, g: 255, b: 255, a: 1 }
            });

            this.addText({
                x: margin,
                y: Math.round(height * 0.08),
                width: width * 0.5,
                height: 24,
                content: 'Company Name',
                color: { r: 30, g: 30, b: 30, a: 1 },
                fontSize: titleSize,
                fontWeight: 700
            });

            this.addText({
                x: margin,
                y: Math.round(height * 0.13),
                width: width * 0.6,
                height: 18,
                content: 'Brand Identity Studio',
                color: { r: 110, g: 110, b: 110, a: 1 },
                fontSize: subtitleSize,
                fontWeight: 500
            });

            this.addText({
                x: margin,
                y: bodyStartY,
                width: width * 0.6,
                height: 16,
                content: '123 Palm Street',
                color: { r: 90, g: 90, b: 90, a: 1 },
                fontSize: bodySize,
                fontWeight: 500
            });

            this.addText({
                x: margin,
                y: bodyStartY + bodyGap,
                width: width * 0.6,
                height: 16,
                content: 'Bengaluru, KA 560001',
                color: { r: 90, g: 90, b: 90, a: 1 },
                fontSize: bodySize,
                fontWeight: 500
            });

            this.addText({
                x: margin,
                y: bodyStartY + bodyGap * 2,
                width: width * 0.6,
                height: 16,
                content: 'India',
                color: { r: 90, g: 90, b: 90, a: 1 },
                fontSize: bodySize,
                fontWeight: 500
            });

            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: margin, y: height - Math.round(height * 0.08) },
                size: { width: width - margin * 2, height: 2 },
                fill: { r: 200, g: 200, b: 200, a: 1 }
            });

            this.addText({
                x: margin,
                y: footerY,
                width: width * 0.6,
                height: 16,
                content: '+91 98765 43210  •  hello@company.com',
                color: { r: 120, g: 120, b: 120, a: 1 },
                fontSize: footerSize,
                fontWeight: 500
            });
            return;
        }

        if (variant === 'bold') {
            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width, height },
                fill: { r: 250, g: 250, b: 250, a: 1 }
            });

            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width, height: Math.round(height * 0.08) },
                fill: { r: 38, g: 70, b: 120, a: 1 }
            });

            this.addText({
                x: margin,
                y: Math.round(height * 0.12),
                width: width * 0.6,
                height: 20,
                content: 'Company Name',
                color: { r: 38, g: 70, b: 120, a: 1 },
                fontSize: titleSize,
                fontWeight: 700
            });

            this.addText({
                x: margin,
                y: Math.round(height * 0.17),
                width: width * 0.6,
                height: 18,
                content: 'Corporate Consulting',
                color: { r: 90, g: 110, b: 145, a: 1 },
                fontSize: subtitleSize,
                fontWeight: 500
            });

            this.addText({
                x: margin,
                y: bodyStartY,
                width: width * 0.7,
                height: 16,
                content: '402 Sky Park, MG Road',
                color: { r: 85, g: 85, b: 85, a: 1 },
                fontSize: bodySize,
                fontWeight: 500
            });

            this.addText({
                x: margin,
                y: bodyStartY + bodyGap,
                width: width * 0.7,
                height: 16,
                content: 'Mumbai, MH 400001',
                color: { r: 85, g: 85, b: 85, a: 1 },
                fontSize: bodySize,
                fontWeight: 500
            });

            this.addText({
                x: margin,
                y: footerY,
                width: width * 0.7,
                height: 16,
                content: 'www.company.com  •  +91 90000 11000',
                color: { r: 90, g: 110, b: 145, a: 1 },
                fontSize: footerSize,
                fontWeight: 600
            });
            return;
        }

        if (variant === 'clean') {
            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: 0, y: 0 },
                size: { width, height },
                fill: { r: 255, g: 255, b: 255, a: 1 }
            });

            this.canvasService.addObject({
                type: 'rectangle',
                position: { x: width - Math.round(width * 0.22), y: 0 },
                size: { width: Math.round(width * 0.22), height: Math.round(height * 0.18) },
                fill: { r: 230, g: 230, b: 230, a: 1 }
            });

            this.addText({
                x: margin,
                y: Math.round(height * 0.1),
                width: width * 0.5,
                height: 20,
                content: 'Company Name',
                color: { r: 90, g: 90, b: 90, a: 1 },
                fontSize: titleSize,
                fontWeight: 600
            });

            this.addText({
                x: margin,
                y: Math.round(height * 0.15),
                width: width * 0.6,
                height: 18,
                content: 'Design & Print Studio',
                color: { r: 130, g: 130, b: 130, a: 1 },
                fontSize: subtitleSize,
                fontWeight: 500
            });

            this.addText({
                x: margin,
                y: bodyStartY,
                width: width * 0.6,
                height: 16,
                content: '8th Floor, Orion Hub',
                color: { r: 100, g: 100, b: 100, a: 1 },
                fontSize: bodySize,
                fontWeight: 500
            });

            this.addText({
                x: margin,
                y: bodyStartY + bodyGap,
                width: width * 0.6,
                height: 16,
                content: 'New Delhi 110001',
                color: { r: 100, g: 100, b: 100, a: 1 },
                fontSize: bodySize,
                fontWeight: 500
            });

            this.addText({
                x: margin,
                y: footerY,
                width: width * 0.6,
                height: 16,
                content: 'hello@company.com  •  011 4000 1234',
                color: { r: 120, g: 120, b: 120, a: 1 },
                fontSize: footerSize,
                fontWeight: 500
            });
            return;
        }

        this.canvasService.addObject({
            type: 'rectangle',
            position: { x: 0, y: 0 },
            size: { width, height },
            fill: { r: 255, g: 255, b: 255, a: 1 }
        });

        this.canvasService.addObject({
            type: 'rectangle',
            position: { x: 0, y: height - Math.round(height * 0.08) },
            size: { width, height: Math.round(height * 0.08) },
            fill: { r: 152, g: 24, b: 45, a: 1 }
        });

        this.addText({
            x: margin,
            y: Math.round(height * 0.1),
            width: width * 0.5,
            height: 20,
            content: 'Company Name',
            color: { r: 100, g: 100, b: 100, a: 1 },
            fontSize: titleSize,
            fontWeight: 600
        });

        this.addText({
            x: margin,
            y: Math.round(height * 0.15),
            width: width * 0.6,
            height: 18,
            content: 'Architecture & Interiors',
            color: { r: 130, g: 130, b: 130, a: 1 },
            fontSize: subtitleSize,
            fontWeight: 500
        });

        this.addText({
            x: margin,
            y: bodyStartY,
            width: width * 0.6,
            height: 16,
            content: '221 Lakeview Road',
            color: { r: 95, g: 95, b: 95, a: 1 },
            fontSize: bodySize,
            fontWeight: 500
        });

        this.addText({
            x: margin,
            y: bodyStartY + bodyGap,
            width: width * 0.6,
            height: 16,
            content: 'Pune, MH 411001',
            color: { r: 95, g: 95, b: 95, a: 1 },
            fontSize: bodySize,
            fontWeight: 500
        });

        this.addText({
            x: margin,
            y: footerY,
            width: width * 0.6,
            height: 16,
            content: 'www.company.com  •  +91 88888 12121',
            color: { r: 245, g: 245, b: 245, a: 1 },
            fontSize: footerSize,
            fontWeight: 600
        });
    }

    private resetCanvasView(): void {
        this.canvasService.setZoom(1);
        this.canvasService.setPan({ x: 0, y: 0 });
    }

    private addText(options: {
        x: number;
        y: number;
        width: number;
        height: number;
        content: string;
        color: { r: number; g: number; b: number; a: number };
        fontSize: number;
        fontWeight: number;
        letterSpacing?: number;
        side?: 'front' | 'back';
    }): void {
        this.canvasService.addObject({
            type: 'text',
            position: { x: options.x, y: options.y },
            size: { width: options.width, height: options.height },
            content: options.content,
            fill: options.color,
            side: options.side,
            textStyle: {
                fontFamily: 'Arial',
                fontSize: options.fontSize,
                fontWeight: options.fontWeight,
                fontStyle: 'normal',
                textAlign: 'left',
                textDecoration: 'none',
                lineHeight: 1.2,
                letterSpacing: options.letterSpacing ?? 0.2
            }
        });
    }
}
